import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import cn from 'clsx'

import { DragIcon } from '@App/static/icons/MarketPanelIcons'

import s from './styles.module.scss'

type ColumnBase<T, K extends keyof T> = {
  title: string
  key: K
  width?: number | string
  maxWidth?: number | string
  render?: (value: T[K], row: T) => React.ReactNode
  className?: string
  headerAlign?: 'start' | 'center' | 'end'
}

export type Column<T> = {
  [K in keyof T]: ColumnBase<T, K>
}[keyof T]

type Primitive = string | number | boolean | null | undefined | symbol | bigint | Date
type GroupableKeys<T> = {
  [P in keyof T]-?: T[P] extends Primitive ? P : never
}[keyof T]

type GroupBy<T, K extends GroupableKeys<T>> = {
  field: K
  renderSectionHeader?: (sectionValue: T[K]) => React.ReactNode
  stickyHeader?: boolean
}
type SortableConfig<T> = {
  enabled: boolean
  onReorder: (items: T[], info: { movedId: string | number; oldIndex: number; newIndex: number }) => void
}
type TableProps<T, K extends GroupableKeys<T>> = {
  columns: Column<T>[]
  data: T[]
  rowKey: (row: T) => string | number
  className?: string
  rowClassName?: (row: T) => string | undefined
  bodyClassName?: string
  headerSeparated?: boolean
  rowHeight?: number
  showHeader?: boolean
  rowHoverVariant?: 'scale' | 'colorized'
  groupBy?: K extends never ? never : GroupBy<T, K>
  onRowClick?: (row: T) => void
  sortable?: SortableConfig<T>
}

type RowProps<T, K extends GroupableKeys<T>> = Pick<
  TableProps<T, K>,
  'rowClassName' | 'rowHeight' | 'rowHoverVariant' | 'columns'
> & { row: T; onClick: () => void }
type SortableRowProps<T, K extends GroupableKeys<T>> = RowProps<T, K> & {
  id: string | number
}

const SortableRow = <T, K extends GroupableKeys<T>>({
  id,
  row,
  rowHeight,
  rowHoverVariant = 'scale',
  columns,
  rowClassName,
  onClick,
}: SortableRowProps<T, K>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, height: rowHeight }}
      className={cn(s.row, s[rowHoverVariant], rowClassName?.(row), isDragging && s.dragging)}
      onClick={onClick}
    >
      {columns.map((col) => {
        const rawValue = (row as any)[col.key]
        return (
          <div
            key={`${col.key.toString()}${col.title}`}
            className={cn(s.cell, col.className)}
            style={{ width: col.width, maxWidth: col.maxWidth }}
          >
            {col.render ? col.render(rawValue, row) : rawValue}
          </div>
        )
      })}
      <div className={cn(s.cell, s.dragCell)} {...attributes} {...listeners}>
        <DragIcon className={s.dragHandle} />
      </div>
    </div>
  )
}

export function Table<T, K extends GroupableKeys<T>>({
  columns,
  data,
  rowKey,
  className,
  rowClassName,
  bodyClassName,
  groupBy,
  headerSeparated = true,
  rowHeight = 40,
  showHeader = true,
  rowHoverVariant = 'scale',
  onRowClick,
  sortable,
}: TableProps<T, K>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = data.findIndex((item) => rowKey(item) === active.id)
    const newIndex = data.findIndex((item) => rowKey(item) === over.id)

    const newData = [...data]
    const [removed] = newData.splice(oldIndex, 1)
    newData.splice(newIndex, 0, removed)

    sortable?.onReorder(newData, { movedId: active.id, oldIndex, newIndex })
  }
  const content = (() => {
    if (!groupBy?.field) {
      if (sortable?.enabled) {
        return data.map((row) => (
          <SortableRow
            id={rowKey(row)}
            row={row}
            key={rowKey(row)}
            rowClassName={rowClassName}
            rowHeight={rowHeight}
            rowHoverVariant={rowHoverVariant}
            columns={columns}
            onClick={() => onRowClick?.(row)}
          />
        ))
      }

      return data.map((row) => (
        <Row
          row={row}
          key={rowKey(row)}
          rowClassName={rowClassName}
          rowHeight={rowHeight}
          rowHoverVariant={rowHoverVariant}
          columns={columns}
          onClick={() => onRowClick?.(row)}
        />
      ))
    }

    const field = groupBy.field as keyof T

    const groups = new Map<T[K], T[]>()
    const order: T[K][] = []

    for (const row of data) {
      const key = row[field] as T[K]
      if (!groups.has(key)) {
        groups.set(key, [])
        order.push(key)
      }
      groups.get(key)!.push(row)
    }

    return order.flatMap((sectionValue) => {
      const rows = groups.get(sectionValue)!

      const headerNode = groupBy.renderSectionHeader?.(sectionValue as any) ?? String(sectionValue ?? '')

      return [
        <div key={`section-${String(sectionValue)}`} className={cn(s.sectionHeader, groupBy.stickyHeader && s.sticky)}>
          {headerNode}
        </div>,
        ...rows.map((row) => (
          <Row
            row={row}
            key={rowKey(row)}
            rowClassName={rowClassName}
            rowHeight={rowHeight}
            rowHoverVariant={rowHoverVariant}
            columns={columns}
            onClick={() => onRowClick?.(row)}
          />
        )),
      ]
    })
  })()

  const tableContent = (
    <div className={cn(s.table, sortable?.enabled && s.sortableTable, className)}>
      {showHeader && (
        <div className={cn(s.header, headerSeparated && s.separated)}>
          {columns.map((col) => (
            <div
              key={`${col.key.toString()}${col.title}`}
              className={cn(s.cell, s.headerCell, col.className, col.headerAlign && s[col.headerAlign])}
              style={{ width: col.width, maxWidth: col.maxWidth }}
            >
              {col.title}
            </div>
          ))}
          {sortable?.enabled && <div className={cn(s.cell, s.headerCell, s.dragHeaderCell)} />}
        </div>
      )}

      <div className={cn(s.body, bodyClassName)}>{content}</div>
    </div>
  )

  if (sortable?.enabled) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={data.map((row) => rowKey(row))} strategy={verticalListSortingStrategy}>
          {tableContent}
        </SortableContext>
      </DndContext>
    )
  }

  return tableContent
}

const Row = <T, K extends GroupableKeys<T>>({
  row,
  rowHeight,
  rowHoverVariant = 'scale',
  columns,
  rowClassName,
  onClick,
}: RowProps<T, K>) => (
  <div className={cn(s.row, s[rowHoverVariant], rowClassName?.(row))} style={{ height: rowHeight }} onClick={onClick}>
    {columns.map((col) => {
      const rawValue = (row as any)[col.key]
      return (
        <div
          key={`${col.key.toString()}${col.title}`}
          className={cn(s.cell, col.className)}
          style={{ width: col.width, maxWidth: col.maxWidth }}
        >
          {col.render ? col.render(rawValue, row) : rawValue}
        </div>
      )
    })}
  </div>
)
